import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, onValue, set, get, update } from 'firebase/database';
import { app } from '../firebase';
import { useNotificationService } from '../services/notificationService';
import { recalculateIfBehind, getPerformanceFeedback } from '../services/tutorService';
import { loadQuizBank } from '../services/rtdbCache';
import { useAuth } from '../hooks/useAuth';
import { resolveInstitucion } from '../config/institutions';
import '../styles/PlanEstudio.css';

const PlanEstudioStudy = () => {
    const { planId } = useParams();
    const navigate = useNavigate();
    const { institucion } = useAuth();
    const resolvedInstitucion = resolveInstitucion(institucion);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [plan, setPlan] = useState(null);
    const [temas, setTemas] = useState([]);
    const [progresoTemas, setProgresoTemas] = useState({});
    const [currentTema, setCurrentTema] = useState(null);
    const [showTemaDetail, setShowTemaDetail] = useState(false);
    const [tutorMessage, setTutorMessage] = useState('');
    const [performanceFeedback, setPerformanceFeedback] = useState('');

    // Servicio de notificaciones
    const notificationService = useNotificationService(user);

    // Verificar autenticación
    useEffect(() => {
        const auth = getAuth(app);
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser(user);
                loadPlanData(user.uid, planId);
            } else {
                navigate('/login');
            }
        });
        return () => unsubscribe();
    }, [navigate, planId]);

    // Cargar datos del plan
    const loadPlanData = async (userId, planId) => {
        try {
            const db = getDatabase(app);

            // Cargar plan
            const planRef = ref(db, `planes_estudio/${userId}/${planId}`);
            const planSnapshot = await get(planRef);
            if (planSnapshot.exists()) {
                const planData = planSnapshot.val();
                setPlan({ id: planId, ...planData });

                // Cargar temas seleccionados para este plan
                const temasArray = await loadTemasForPlan(planData.temasSeleccionados || []);
                
                // Cargar progreso
                const progresoData = await loadProgreso(userId, planId);
                
                // Ejecutar tutor
                await runTutor(userId, planId, planData, temasArray, progresoData);

            } else {
                alert('Plan de estudio no encontrado');
                navigate('/dashboard/plan-estudio');
            }
        } catch (error) {
            console.error('Error cargando plan:', error);
            alert('Error al cargar el plan de estudio');
        } finally {
            setLoading(false);
        }
    };

    // Lógica del tutor para recalcular fechas y dar feedback
    const runTutor = async (userId, planId, planData, temasArray, progresoData) => {
        try {
            // Preparar datos para el tutor
            const temasToTutor = temasArray.map(t => {
                const prog = progresoData[t.id];
                return {
                    id: t.id,
                    estado: prog?.completado ? 'COMPLETADO' : 'PENDIENTE',
                    fechaAsignada: prog?.fechaAsignada
                };
            });

            // Recalcular si está atrasado
            const tutorResult = recalculateIfBehind(planData, temasToTutor);
            
            // Si el tutor hizo cambios (asignó nuevas fechas), actualizamos en BD
            if (tutorResult.updatedTemas && tutorResult.updatedTemas.some(t => {
                const oldProg = progresoData[t.id];
                return t.fechaAsignada && t.fechaAsignada !== oldProg?.fechaAsignada;
            })) {
                const db = getDatabase(app);
                const updates = {};
                tutorResult.updatedTemas.forEach(t => {
                    if (t.estado === 'PENDIENTE' && t.fechaAsignada) {
                        updates[`${t.id}/fechaAsignada`] = t.fechaAsignada;
                        updates[`${t.id}/estado`] = 'PENDIENTE';
                    }
                });
                
                if (Object.keys(updates).length > 0) {
                    await update(ref(db, `progreso_estudio/${userId}/${planId}`), updates);
                    // Recargar el progreso para reflejar los cambios
                    const newProgreso = await loadProgreso(userId, planId);
                    setProgresoTemas(newProgreso);
                }
            }
            
            setTutorMessage(tutorResult.message);

            // Generar feedback de rendimiento si hay datos
            // Suponemos una calificación mock o sacada del progreso general
            // Idealmente aquí se calcularía el % de aprovechamiento (ej. quiz scores)
            const progresoPct = planData.progreso || 0;
            if (progresoPct > 0) {
                // Mock: asignamos una calificación basada en progreso
                const aprovechamiento = (progresoPct / 10); // 0 a 10
                setPerformanceFeedback(getPerformanceFeedback(aprovechamiento));
            }

        } catch (err) {
            console.error("Error ejecutando tutor:", err);
        }
    };

    // Cargar temas seleccionados para el plan
    const loadTemasForPlan = async (temasSeleccionadosIds) => {
        try {
            const bank = await loadQuizBank({ institucion: resolvedInstitucion });
            const temasArray = (bank.temas || [])
                .map((tema) => ({
                    ...tema,
                    id: parseInt(tema.id, 10)
                }))
                .filter(tema => temasSeleccionadosIds.includes(tema.id));

            setTemas(temasArray);
            return temasArray;
        } catch (error) {
            console.error('Error cargando temas:', error);
            return [];
        }
    };

    // Cargar progreso del usuario
    const loadProgreso = async (userId, planId) => {
        try {
            const db = getDatabase(app);
            const progresoRef = ref(db, `progreso_estudio/${userId}/${planId}`);
            const progresoSnapshot = await get(progresoRef);

            if (progresoSnapshot.exists()) {
                const progresoData = progresoSnapshot.val();
                setProgresoTemas(progresoData);
                return progresoData;
            }
            return {};
        } catch (error) {
            console.error('Error cargando progreso:', error);
            return {};
        }
    };

    // Marcar tema como completado
    const marcarTemaCompletado = async (temaId, completado) => {
        if (!user || !plan) return;

        try {
            const db = getDatabase(app);
            const progresoRef = ref(db, `progreso_estudio/${user.uid}/${plan.id}/${temaId}`);

            const progresoData = {
                completado: completado,
                fechaCompletado: completado ? new Date().toISOString() : null,
                tiempoEstudiado: progresoTemas[temaId]?.tiempoEstudiado || 0
            };

            await set(progresoRef, progresoData);

            // Actualizar estado local
            setProgresoTemas(prev => ({
                ...prev,
                [temaId]: progresoData
            }));

            // Actualizar estadísticas del plan
            await actualizarEstadisticasPlan();
        } catch (error) {
            console.error('Error actualizando progreso:', error);
        }
    };

    // Actualizar estadísticas del plan
    const actualizarEstadisticasPlan = async () => {
        if (!user || !plan) return;

        try {
            const temasCompletados = Object.values(progresoTemas).filter(p => p.completado).length;
            const progreso = temas.length > 0 ? Math.round((temasCompletados / temas.length) * 100) : 0;

            const db = getDatabase(app);
            const planRef = ref(db, `planes_estudio/${user.uid}/${plan.id}`);

            await update(planRef, {
                progreso: progreso,
                temasCompletados: temasCompletados,
                totalTemas: temas.length,
                fechaActualizacion: new Date().toISOString()
            });

            // Actualizar estado local
            setPlan(prev => ({
                ...prev,
                progreso: progreso,
                temasCompletados: temasCompletados,
                totalTemas: temas.length
            }));

            // Generar notificaciones por hitos de progreso
            const progresoAnterior = plan.progreso || 0;
            if (progreso > progresoAnterior && progreso > 0) {
                // Notificar progreso alcanzado
                await notificationService.notifyThemeCompleted(plan.nombre, '', progreso);

                // Notificaciones especiales por porcentajes
                if (progreso === 25 && progresoAnterior < 25) {
                    await notificationService.notifyAchievement(
                        'Primer Paso',
                        'Has completado el 25% de tu plan de estudio. ¡Sigue así!'
                    );
                } else if (progreso === 50 && progresoAnterior < 50) {
                    await notificationService.notifyAchievement(
                        'Mitad del Camino',
                        'Has alcanzado el 50% de progreso. ¡Estás a mitad de camino!'
                    );
                } else if (progreso === 75 && progresoAnterior < 75) {
                    await notificationService.notifyAchievement(
                        'Casi Listo',
                        'Has completado el 75% de tu plan. ¡El final está cerca!'
                    );
                }
            }

            // Recordatorios de estudio si hay temas pendientes
            const temasPendientes = temas.length - temasCompletados;
            if (temasPendientes > 0 && progreso < 100) {
                await notificationService.createStudyReminders(plan.nombre, temasPendientes);
            }

        } catch (error) {
            console.error('Error actualizando estadísticas:', error);
        }
    };

    // Ver detalle de tema
    const verTemaDetail = (tema) => {
        setCurrentTema(tema);
        setShowTemaDetail(true);
    };

    // Calcular estadísticas
    const calcularEstadisticas = () => {
        const totalTemas = temas.length;
        const completados = Object.values(progresoTemas).filter(p => p.completado).length;
        const progreso = totalTemas > 0 ? Math.round((completados / totalTemas) * 100) : 0;

        return { totalTemas, completados, progreso };
    };

    const stats = calcularEstadisticas();

    if (loading) {
        return <div className="plan-study-container"><div className="loading">Cargando plan de estudio...</div></div>;
    }

    if (!plan) {
        return <div className="plan-study-container"><div className="error-message">Plan de estudio no encontrado</div></div>;
    }

    return (
        <div className="plan-study-container">
            <div className="study-header">
                <button className="back-button" onClick={() => navigate('/dashboard/plan-estudio')}>
                    ← Volver a Planes
                </button>
                <h1>{plan.nombre}</h1>
            </div>

            {/* Panel del Tutor */}
            <div className="tutor-panel" style={{
                background: 'rgba(10, 26, 42, 0.85)',
                border: '1px solid var(--gold-matte)',
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '2rem',
                boxShadow: '0 0 15px rgba(197, 160, 89, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem'
            }}>
                <h3 style={{ margin: 0, color: 'var(--gold-matte)', fontFamily: 'var(--font-tactical)', fontSize: '0.9rem', letterSpacing: '2px' }}>
                    🤖 Tutor IA Vortex
                </h3>
                {tutorMessage && (
                    <p style={{ margin: '0.5rem 0', color: 'var(--text-primary)', fontSize: '0.9rem', lineHeight: '1.5' }}>
                        {tutorMessage}
                    </p>
                )}
                {performanceFeedback && (
                    <p style={{ margin: 0, color: '#4caf50', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        {performanceFeedback}
                    </p>
                )}
            </div>

            <div className="study-stats">
                <div className="stat-card">
                    <div className="stat-number">{stats.progreso}%</div>
                    <div className="stat-label">Progreso General</div>
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${stats.progreso}%` }}></div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-number">{stats.completados}</div>
                    <div className="stat-label">Temas Completados</div>
                    <div className="stat-subtext">de {stats.totalTemas} total</div>
                </div>

                <div className="stat-card">
                    <div className="stat-number">{stats.totalTemas - stats.completados}</div>
                    <div className="stat-label">Temas Pendientes</div>
                    <div className="stat-subtext">por estudiar</div>
                </div>
            </div>

            <div className="temas-section">
                <h2>📚 Temas de Estudio</h2>

                <div className="temas-grid">
                    {temas.map(tema => {
                        const progreso = progresoTemas[tema.id];
                        const completado = progreso?.completado || false;

                        return (
                            <div key={tema.id} className={`tema-card ${completado ? 'completed' : 'pending'}`}>
                                <div className="tema-header">
                                    <h3>{tema.nombre}</h3>
                                    <div className={`tema-status ${completado ? 'completed' : 'pending'}`}>
                                        {completado ? '✅ Completado' : '⏳ Pendiente'}
                                    </div>
                                </div>

                                <div className="tema-area">
                                    Área: {(() => {
                                        // Buscar el nombre del área
                                        const db = getDatabase(app);
                                        // Esta es una simplificación - en producción necesitarías cargar las áreas
                                        const areaNames = {
                                            1: 'Conocimientos Marineros',
                                            2: 'Legislación',
                                            3: 'Conocimientos Específicos',
                                            4: 'Oficiales'
                                        };
                                        return areaNames[tema.area_id] || `Área ${tema.area_id}`;
                                    })()}
                                </div>

                                {progreso?.fechaCompletado ? (
                                    <div className="tema-date">
                                        Completado: {new Date(progreso.fechaCompletado).toLocaleDateString()}
                                    </div>
                                ) : progreso?.fechaAsignada ? (
                                    <div className="tema-date" style={{ color: progreso.fechaAsignada < new Date().toISOString().split('T')[0] ? '#ff5252' : 'var(--gold-matte)' }}>
                                        📅 Asignado para: {new Date(progreso.fechaAsignada).toLocaleDateString()}
                                    </div>
                                ) : null}

                                <div className="tema-actions">
                                    <button
                                        className="study-tema-btn"
                                        onClick={() => verTemaDetail(tema)}
                                    >
                                        📖 Estudiar Tema
                                    </button>

                                    <button
                                        className={`complete-btn ${completado ? 'undo' : 'complete'}`}
                                        onClick={() => marcarTemaCompletado(tema.id, !completado)}
                                    >
                                        {completado ? '↶ Marcar como Pendiente' : '✅ Marcar como Completado'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {showTemaDetail && currentTema && (
                <div className="tema-detail-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>{currentTema.nombre}</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowTemaDetail(false)}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="tema-info">
                                <p><strong>Área:</strong> {(() => {
                                    const areaNames = {
                                        1: 'Conocimientos Marineros',
                                        2: 'Legislación',
                                        3: 'Conocimientos Específicos',
                                        4: 'Oficiales'
                                    };
                                    return areaNames[currentTema.area_id] || `Área ${currentTema.area_id}`;
                                })()}</p>

                                <p><strong>ID del Tema:</strong> {currentTema.id}</p>
                            </div>

                            <div className="tema-content">
                                <h3>Contenido del Tema</h3>
                                <p>Este tema incluye conceptos importantes relacionados con {currentTema.nombre.toLowerCase()}.</p>

                                <div className="tema-actions">
                                    <button
                                        className="quiz-btn"
                                        onClick={() => navigate(`/dashboard/quiz/${currentTema.id}`)}
                                    >
                                        📝 Hacer Quiz del Tema
                                    </button>

                                    <button
                                        className="simulator-btn"
                                        onClick={() => navigate('/dashboard/simulador')}
                                    >
                                        🎯 Ir al Simulador
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlanEstudioStudy;